<template>
    <v-container :fluid="true" class="max-height">
        <v-row>
            <v-col class="px-5 d-flex justify-space-between" cols="12">
                <h1 class="text-left">OTA Packages</h1>
                <div class="d-flex align-center ga-3">
                    <v-btn-toggle v-model="showFilter" density="compact" variant="outlined" color="primary"
                                  @update:model-value="currentPage = 1; fetchPackages()">
                        <v-btn value="disabled" size="small">
                            <v-icon start :icon="showFilter === 'disabled' ? 'mdi-eye' : 'mdi-eye-off'"/>{{ showFilter === 'disabled' ? 'Hide Disabled' : 'Show Disabled' }}
                        </v-btn>
                        <v-btn value="trashed" size="small">
                            <v-icon start :icon="showFilter === 'trashed' ? 'mdi-delete-restore' : 'mdi-delete-outline'"/>{{ showFilter === 'trashed' ? 'Hide Deleted' : 'Show Deleted' }}
                        </v-btn>
                    </v-btn-toggle>
                    <v-btn color="black" prepend-icon="mdi-plus" @click="selectedPackage = null; dialogSwitch = true">Add
                    </v-btn>
                </div>
            </v-col>
        </v-row>
        <h3></h3>
        <v-row>
            <v-col cols="12">
                <v-table>
                    <thead>
                    <tr>
                        <th class="cursor-pointer" @click="toggleSort('id')">
                            ID <v-icon v-if="sortBy === 'id'" :icon="sortIcon" size="x-small"/>
                        </th>
                        <th class="cursor-pointer" @click="toggleSort('sku')">
                            Product SKU <v-icon v-if="sortBy === 'sku'" :icon="sortIcon" size="x-small"/>
                        </th>
                        <th class="cursor-pointer" @click="toggleSort('label')">
                            Package Name <v-icon v-if="sortBy === 'label'" :icon="sortIcon" size="x-small"/>
                        </th>
                        <th class="cursor-pointer" @click="toggleSort('version')">
                            Package Version <v-icon v-if="sortBy === 'version'" :icon="sortIcon" size="x-small"/>
                        </th>
                        <th>Notes</th>
                        <th>Actions</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="otaPackage in otaPackages" v-if="otaPackages.length" :key="otaPackage.id"
                        class="text-left" :class="{'bg-red-lighten-5': otaPackage.deleted_at}">
                        <td>{{ otaPackage.id }}</td>
                        <td>{{ otaPackage.sku }}</td>
                        <td>{{ otaPackage.label }}</td>
                        <td>{{ otaPackage.version }}</td>
                        <td class="text-caption" style="max-width: 200px;">
                            <v-tooltip v-if="otaPackage.notes" :text="otaPackage.notes" location="top" max-width="300">
                                <template v-slot:activator="{ props }">
                                    <span v-bind="props" class="d-inline-block text-truncate" style="max-width: 200px;">{{ otaPackage.notes }}</span>
                                </template>
                            </v-tooltip>
                        </td>
                        <td>
                            <v-btn-group v-if="!otaPackage.deleted_at" density="compact" divided variant="outlined">
                                <v-btn :disabled="dialogSwitch || otaPackage.active_campaign_count > 0" class="text-warning" prepend-icon="mdi-pencil"
                                       size="x-small" @click="editPackage(otaPackage)">Edit
                                    <v-tooltip v-if="otaPackage.active_campaign_count > 0" activator="parent" location="top">Disable active campaign before editing</v-tooltip>
                                </v-btn>
                                <v-btn :class="otaPackage.status ? 'text-grey' : 'text-success'" size="x-small"
                                       :prepend-icon="otaPackage.status ? 'mdi-eye-off' : 'mdi-eye'"
                                       @click="togglePackageStatus(otaPackage)">{{ otaPackage.status ? 'Disable' : 'Enable' }}
                                </v-btn>
                                <v-btn :disabled="otaPackage.active_campaign_count > 0" class="text-error" prepend-icon="mdi-delete" size="x-small"
                                       @click="deletePackage(otaPackage)">Delete
                                    <v-tooltip v-if="otaPackage.active_campaign_count > 0" activator="parent" location="top">Disable active campaign before deleting</v-tooltip>
                                </v-btn>
                            </v-btn-group>
                            <v-btn-group v-else density="compact" divided variant="outlined">
                                <v-menu>
                                    <template v-slot:activator="{ props }">
                                        <v-btn icon="mdi-dots-vertical" v-bind="props" variant="text" size="x-small"/>
                                    </template>
                                    <v-list density="compact">
                                        <v-list-item
                                            title="Permanently Delete"
                                            prepend-icon="mdi-delete-forever"
                                            class="text-error"
                                            @click="forceDeletePackage(otaPackage)"/>
                                    </v-list>
                                </v-menu>
                            </v-btn-group>
                        </td>
                    </tr>
                    <tr v-else>
                        <td v-if="loading" colspan="6">
                            <v-progress-circular color="primary" indeterminate/>
                        </td>
                        <td v-else colspan="6">No record found</td>
                    </tr>
                    </tbody>
                </v-table>
            </v-col>
        </v-row>
        <v-row v-if="totalPages > 1" justify="center">
            <v-col cols="8">
                <v-pagination
                    v-model="currentPage"
                    :length="totalPages"
                    class="my-4"
                    density="compact"
                    @update:model-value="fetchPackages"
                />
            </v-col>
        </v-row>
    </v-container>

    <otaPackage v-if="dialogSwitch" v-model="dialogSwitch" :otaPackage="selectedPackage"
                @addPackage="(newPackage)=> otaPackages.unshift(newPackage)"
                @updatePackage="(updatedPackage) => {
                    const idx = otaPackages.findIndex(p => p.id === updatedPackage.id);
                    if (idx !== -1) otaPackages[idx] = updatedPackage;
                }"/>
</template>

<script setup>
import {onMounted, ref, computed} from "vue";
import {send} from "@/composables/apiClient";
import {useSnackbarStore} from "@/stores/snackBarStore";
import OtaPackage from "@/components/dialogs/ota/Package.vue";

const loading = ref(true);
const totalPages = ref(1);
const currentPage = ref(1);
const otaPackages = ref([]);
const {showSnackbar} = useSnackbarStore();
const dialogSwitch = ref(false);
const selectedPackage = ref(null);
const showFilter = ref(undefined);
const sortBy = ref('updated_at');
const sortDir = ref('desc');
const sortIcon = computed(() => sortDir.value === 'asc' ? 'mdi-arrow-up' : 'mdi-arrow-down');

const toggleSort = (column) => {
    if (sortBy.value === column) {
        sortDir.value = sortDir.value === 'asc' ? 'desc' : 'asc';
    } else {
        sortBy.value = column;
        sortDir.value = 'asc';
    }
    currentPage.value = 1;
    fetchPackages();
};

onMounted(() => {
    fetchPackages();
});

const fetchPackages = () => {
    const params = {page: currentPage.value, sort_by: sortBy.value, sort_dir: sortDir.value};
    if (showFilter.value === 'disabled') params.show_disabled = 1;
    if (showFilter.value === 'trashed') params.show_trashed = 1;
    send('get', `admin/ota/packages`, params)
        .then((response) => {
            otaPackages.value = response.data.data.data;
            totalPages.value = response.data.data.last_page;
            currentPage.value = response.data.data.current_page;
        })
        .catch((error) => {
            console.log(error)
            showSnackbar('OTA Packages could not be loaded', 'error')
        })
        .finally(() => {
            loading.value = false
        })
}
const editPackage = (otaPackage) => {
    selectedPackage.value = otaPackage;
    dialogSwitch.value = true;
}
const togglePackageStatus = (otaPackage) => {
    const newStatus = otaPackage.status ? 0 : 1;
    send('put', `admin/ota/packages/${otaPackage.id}`, {status: newStatus})
        .then((response) => {
            if (response.data.success) {
                if (!showFilter.value && !newStatus) {
                    const idx = otaPackages.value.findIndex(p => p.id === otaPackage.id);
                    if (idx !== -1) otaPackages.value.splice(idx, 1);
                } else {
                    otaPackage.status = newStatus;
                }
                showSnackbar(response.data.message ?? `OTA Package ${newStatus ? 'enabled' : 'disabled'}`);
            }
        })
        .catch((error) => {
            showSnackbar(error.response?.data?.message ?? 'Could not update status', 'error');
        });
}
const deletePackage = (otaPackage) => {
    if (confirm(`Are you sure to delete "${otaPackage.label}" OTA Package?`)) {
        send('delete', `admin/ota/packages/${otaPackage.id}`)
            .then((response) => {
                if (response.data.success) {
                    const idx = otaPackages.value.findIndex(p => p.id === otaPackage.id);
                    if (idx !== -1) otaPackages.value.splice(idx, 1);
                    showSnackbar(response.data.message);
                }
            })
            .catch((error) => {
                if (error.response?.data?.message) {
                    showSnackbar(error.response.data.message, 'error')
                } else {
                    showSnackbar(`"${otaPackage.label}" OTA Package could not be deleted`, 'error')
                }
            })
    }
}
const forceDeletePackage = (otaPackage) => {
    if (!confirm(`Permanently delete "${otaPackage.label}"? This will also remove all related campaigns, device statuses, and logs. This cannot be undone.`)) return;
    send('post', `admin/ota/packages/${otaPackage.id}/force-delete`)
        .then((response) => {
            if (response.data.success) {
                const idx = otaPackages.value.findIndex(p => p.id === otaPackage.id);
                if (idx !== -1) otaPackages.value.splice(idx, 1);
                showSnackbar(response.data.message);
            }
        })
        .catch((error) => showSnackbar(error.response?.data?.message || 'Failed to permanently delete package', 'error'));
}
</script>
